import {
  AlertTriangle,
  Award,
  BarChart3,
  CheckCircle2,
  Clock,
  FileSearch,
  Linkedin,
  Shield,
  TrendingUp,
  Users,
} from "lucide-react";
import { ImageWithFallback } from "./ImageWithFallback";
import { Logo } from "./LogoVariants";

export const CTASection = () => {
  return (
    <section
      id="contact"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-purple-700 text-white"
    >
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="mb-6 text-white">
          Ready to Transform Your Tax Compliance?
        </h2>
        <p className="text-xl mb-8 text-blue-100">
          Join forward-thinking accountants who are already using TaxMate to
          catch compliance issues before they become costly problems.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <input
            type="email"
            placeholder="Enter your email address"
            className="px-6 py-4 rounded-lg text-gray-800 w-full sm:w-96 focus:outline-none focus:ring-2 focus:ring-white"
          />
          <button className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:shadow-xl transition-shadow font-semibold">
            Start Free Trial
          </button>
        </div>
        <p className="text-blue-100">
          No credit card required • 14-day free trial • Setup in 5 minutes
        </p>
        <div className="grid md:grid-cols-3 gap-8 mt-16 pt-16 border-t border-blue-400">
          <div>
            <div className="text-4xl font-bold mb-2">100%</div>
            <p className="text-blue-100">KRA Regulation Coverage</p>
          </div>
          <div>
            <div className="text-4xl font-bold mb-2">24/7</div>
            <p className="text-blue-100">Automated Monitoring</p>
          </div>
          <div>
            <div className="text-4xl font-bold mb-2">5 min</div>
            <p className="text-blue-100">Average Setup Time</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FounderSection = () => {
  return (
    <section
      id="founder"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="mb-4">Meet Our Founder</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            TaxMate is built by professionals who understand the real challenges
            accountants face every day.
          </p>
        </div>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2 p-8 bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1614023342667-6f060e9d1e04?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMGFmcmljYW4lMjBlbnRyZXByZW5ldXJ8ZW58MXx8fHwxNzc5OTc2NDMyfDA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Eliud Ngige - Founder of TaxMate"
                className="rounded-xl w-full h-full object-cover"
              />
            </div>
            <div className="md:col-span-3 p-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="mb-1">Eliud Ngige</h3>
                  <p className="text-blue-600 font-semibold mb-2">
                    Founder & CEO
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/eliud-ngige-65299424/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                >
                  <Linkedin className="w-5 h-5 text-white" />
                </a>
              </div>
              <div className="space-y-4 text-gray-600">
                <p>
                  Eliud Ngige brings extensive experience in finance,
                  technology, and regulatory compliance to TaxMate. His deep
                  understanding of Kenyan tax systems and the challenges
                  accountants face has shaped TaxMate into the practical,
                  powerful solution it is today.
                </p>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <p>
                      Proven track record in financial services and technology
                      innovation
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <p>
                      Deep expertise in KRA regulations and compliance
                      requirements
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <p>
                      Committed to empowering accountants with intelligent
                      automation
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="italic text-gray-700">
                    "Every accountant deserves to sleep well at night, knowing
                    their clients' tax filings are accurate and compliant.
                    That's why we built TaxMate."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const LogoSection = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="mb-4">Our Brand Identity</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Each logo variation represents a core aspect of TaxMate's mission to
            protect and empower accountants.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="flex flex-col items-center p-8 bg-gray-50 rounded-xl">
            <Logo variant="shield" size="lg" showText={false} />
            <p className="mt-4 font-semibold text-gray-800">Shield</p>
            <p className="text-sm text-gray-600 text-center mt-2">
              Protection from compliance risks
            </p>
          </div>
          <div className="flex flex-col items-center p-8 bg-gray-50 rounded-xl">
            <Logo variant="trend" size="lg" showText={false} />
            <p className="mt-4 font-semibold text-gray-800">Trend</p>
            <p className="text-sm text-gray-600 text-center mt-2">
              Intelligence and insights
            </p>
          </div>
          <div className="flex flex-col items-center p-8 bg-gray-50 rounded-xl">
            <Logo variant="check" size="lg" showText={false} />
            <p className="mt-4 font-semibold text-gray-800">Check</p>
            <p className="text-sm text-gray-600 text-center mt-2">
              Verified compliance
            </p>
          </div>
          <div className="flex flex-col items-center p-8 bg-gray-50 rounded-xl">
            <Logo variant="calculator" size="lg" showText={false} />
            <p className="mt-4 font-semibold text-gray-800">Calculator</p>
            <p className="text-sm text-gray-600 text-center mt-2">
              Tax precision
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const HowItWorksSection = () => {
  return (
    <section
      id="how-it-works"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="mb-4">How TaxMate Works</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Getting started is simple. TaxMate integrates with your existing
            accounting workflow in three easy steps.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="relative">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                1
              </div>
              <h3 className="mb-3">Connect Your Data</h3>
              <p className="text-gray-600 mb-4">
                Link your accounting software or upload transaction files.
                TaxMate supports Excel, CSV, and all major accounting platforms.
              </p>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1709880945165-d2208c6ad2ec?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwyfHx0YXglMjBjb21wbGlhbmNlJTIwYWNjb3VudGluZyUyMHByb2Zlc3Npb25hbCUyMG9mZmljZXxlbnwxfHx8fDE3Nzk5NzY0MzJ8MA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Calculator and laptop on desk"
                className="rounded-lg w-full h-48 object-cover"
              />
            </div>
          </div>
          <div className="relative">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                2
              </div>
              <h3 className="mb-3">AI Analyzes Everything</h3>
              <p className="text-gray-600 mb-4">
                Our intelligent risk engine scans every transaction across all
                KRA tax heads, identifying potential compliance issues
                instantly.
              </p>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwyfHx0ZWNobm9sb2d5JTIwZGFzaGJvYXJkJTIwYW5hbHl0aWNzfGVufDF8fHx8MTc3OTk3NjQzM3ww&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Analytics dashboard on laptop"
                className="rounded-lg w-full h-48 object-cover"
              />
            </div>
          </div>
          <div className="relative">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center mb-4 font-bold">
                3
              </div>
              <h3 className="mb-3">Review & Fix Issues</h3>
              <p className="text-gray-600 mb-4">
                Get a clear dashboard with risk scores, alerts, and step-by-step
                recommendations to resolve issues before filing with KRA.
              </p>
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1707902665498-a202981fb5ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHw0fHx0YXglMjBjb21wbGlhbmNlJTIwYWNjb3VudGluZyUyMHByb2Zlc3Npb25hbCUyMG9mZmljZXxlbnwxfHx8fDE3Nzk5NzY0MzJ8MA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Person working at desk with calculator"
                className="rounded-lg w-full h-48 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FeaturesSection = () => {
  return (
    <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="mb-4">TaxMate Features Built for KRA Compliance</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our intelligent platform automatically scans your business data
            across all tax heads, identifying risks before they become problems.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <ImageWithFallback
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwzfHx0YXglMjBjb21wbGlhbmNlJTIwYWNjb3VudGluZyUyMHByb2Zlc3Npb25hbCUyMG9mZmljZXxlbnwxfHx8fDE3Nzk5NzY0MzJ8MA&ixlib=rb-4.1.0&q=80&w=1080"
              alt="Person reviewing tax documents with calculator"
              className="rounded-2xl shadow-xl w-full object-cover"
            />
          </div>
          <div>
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="mb-2">Multi-Tax Head Risk Engine</h3>
                <p className="text-gray-600">
                  Automatically evaluates your data across VAT, PAYE,
                  Withholding Tax, and Income Tax, catching cross-tax
                  discrepancies that manual reviews miss.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="mb-2">Clear Risk Scores & Alerts</h3>
                <p className="text-gray-600">
                  Get instant, easy-to-understand compliance scores with
                  color-coded alerts highlighting exactly where attention is
                  needed before filing.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="mb-2">Actionable Recommendations</h3>
                <p className="text-gray-600">
                  Don't just get alerts—get specific, step-by-step actions to
                  fix issues, complete with KRA regulation references and
                  resolution guides.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="mb-2">Real-Time Monitoring</h3>
            <p className="text-gray-600">
              Continuous scanning of your transactions as they happen, not just
              at filing time.
            </p>
          </div>
          <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
              <FileSearch className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="mb-2">Audit Trail Reports</h3>
            <p className="text-gray-600">
              Complete documentation of all compliance checks for KRA audits and
              client reviews.
            </p>
          </div>
          <div className="text-center p-8 bg-gradient-to-br from-pink-50 to-orange-50 rounded-xl">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
              <Users className="w-8 h-8 text-pink-600" />
            </div>
            <h3 className="mb-2">Multi-Client Dashboard</h3>
            <p className="text-gray-600">
              Manage compliance for all your clients in one place with
              individual risk profiles.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const ProblemStatement = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="mb-4">The Challenges Accountants Face with KRA</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Every day, accountants struggle with complex compliance requirements
            that can lead to costly penalties and client dissatisfaction.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="mb-3">Late Discovery of Errors</h3>
            <p className="text-gray-600">
              Finding tax discrepancies after filing means penalties,
              amendments, and difficult conversations with clients.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="mb-3">Manual Review Takes Too Long</h3>
            <p className="text-gray-600">
              Checking every transaction across multiple tax heads is
              time-consuming and prone to human oversight.
            </p>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
              <FileSearch className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="mb-3">Complex KRA Requirements</h3>
            <p className="text-gray-600">
              Navigating VAT, PAYE, Withholding Tax, and Income Tax rules
              simultaneously is overwhelming and confusing.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const HeroSection = () => {
  return (
    <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full mb-6">
              Tax Intelligence for Kenyan Accountants
            </div>
            <h1 className="mb-6">Stop Worrying About KRA Compliance Issues</h1>
            <p className="text-xl text-gray-600 mb-8">
              TaxMate is your AI-powered tax intelligence platform that catches
              compliance risks before you file, giving you confidence and peace
              of mind with every KRA submission.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="#contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-xl transition-shadow text-center"
              >
                Start Free Trial
              </a>
              <a
                href="#how-it-works"
                className="px-8 py-4 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-center"
              >
                See How It Works
              </a>
            </div>
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-gray-600">No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-gray-600">Setup in 5 minutes</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl blur-2xl opacity-20"></div>
            <ImageWithFallback
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWNobm9sb2d5JTIwZGFzaGJvYXJkJTIwYW5hbHl0aWNzfGVufDF8fHx8MTc3OTk3NjQzM3ww&ixlib=rb-4.1.0&q=80&w=1080"
              alt="TaxMate dashboard showing analytics and compliance scores"
              className="relative rounded-2xl shadow-2xl w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export const ComingSoonSection = () => {
  return (
    <section
      id="founder"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-white"
    >
      <div className="max-w-7xl mx-auto">
        <h1 className="coming-soon mb-4">Coming Soon</h1>
      </div>
    </section>
  );
};
